import { Component, DestroyRef, inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { UserService } from 'src/app/core/api/user.service';

import { CohortService } from '../../../core/api/cohort.service';
import { RegionService } from '../../../core/api/region.service';
import { SiteService } from '../../../core/api/site.service';
import { FormatHelperService } from '../../../core/helper/format-helper.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { RoleEnum } from '../../../shared/constants/role.enums';
import { StatusEnum } from '../../../shared/constants/site_status.enum';
import { Region, Site, User } from '../../../shared/models';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import * as fromApp from '../../../store';

@Component({
  selector: 'fc-stats-dashboard',
  templateUrl: './stats-dashboard.component.html',
  styleUrls: ['./stats-dashboard.component.scss'],
})
export class StatsDashboardComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  @ViewChildren(MatTab) tabs!: QueryList<MatTab>;

  users: User[] = [];
  sites: Site[] = [];
  adminTypeData: any;
  userStatusData: any;
  userBuyerData: any;
  pendingSiteData: any;
  submittedSiteData: any;
  activeSiteData: any;
  inactiveSiteData: any;
  overallSiteData: any;
  totalCounts: [] = [];
  siteStats: {
    netErt: number;
    totalAcres: number;
    ertPerAcre: number;
    acresPerSite: number;
    averageAnnual: number;
    total: number;
  } = {
    netErt: 0,
    totalAcres: 0,
    ertPerAcre: 0,
    acresPerSite: 0,
    averageAnnual: 0,
    total: 0,
  };

  applyFilterStatus$: Subject<{ event: Event; column: string }> = new Subject();
  applyFilterRegion$: Subject<{ event: Event; section: string }> = new Subject();

  sortStatusArray = [];
  allRegions: Region[] = [];
  filterStatusState = [];
  filterRegionState: any = {};
  sortSteps = [
    'Stopped at Site Definition',
    'Stopped at Parcel Selection',
    'Stopped at Stand Selection',
    'Stopped at Eligibility Check',
    'Stopped at Offer Selection',
    'Stopped at Documentation',
    'Stopped at Review',
    'Stopped at Submit',
    'Requires Landowner Revision',
    'Unknown',
    'Dormant',
  ];
  sortUserStatus = [
    'Never Signed In',
    'No ID',
    'No Sites',
    'Only Cancelled',
    'Only Pending',
    'Only Submitted',
    'Has Active',
  ];

  constructor(
    public dialog: MatDialog,
    readonly siteService: SiteService,
    readonly siteHelper: SiteHelperService,
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    readonly userService: UserService,
    readonly regionService: RegionService,
    readonly cohortService: CohortService,
    readonly formatService: FormatHelperService,
    readonly spinnerService: SpinnerService
  ) {}

  ngOnInit() {
    this.sortStatusArray = this.siteHelper.siteActiveStatusList.concat(this.siteHelper.siteSubmittedStatusList).sort();
    this.filterStatusState = this.siteHelper.siteActiveStatusList;
    this.regionService.getAllRegions().subscribe(allRegions => {
      this.allRegions = allRegions;
      this.filterRegionState['valuationSummary'] = this.allRegions.map(region => region.id);
      this.filterRegionState['siteSummary'] = this.allRegions.map(region => region.id);
    });

    this.spinnerService.show('user-spinner', true, 'Loading Users');
    this.userService.getUsersForStatDashboard().subscribe(users => {
      this.users = users;
      this.totalCounts['Users'] = users.length;
      this.totalCounts['Landowners'] = users.filter(u => u.isLandowner).length;
      this.totalCounts['Admins'] = users.filter(u => u.isAdmin || u.isLimitedAdmin).length;
      this.adminTypeData = this.formatMapWithValue([
        {
          value: users.filter(user => user.hasRole(RoleEnum.Admin)).length,
          name: 'Regular',
        },
        {
          value: users.filter(user => user.hasRole(RoleEnum.SuperAdmin)).length,
          name: 'Super',
        },
        {
          value: users.filter(user => user.hasRole(RoleEnum.LimitedAdmin)).length,
          name: 'Limited',
        },
        {
          value: users.filter(user => user.hasRole(RoleEnum.FinanceAdmin)).length,
          name: 'Finance',
        },
      ]);
      this.userStatusData = this.formatReduceWithValue(
        users.reduce((count, u) => {
          if (u.submittedStatus) {
            count[u.submittedStatus] = (count[u.submittedStatus] || 0) + 1;
          }
          return count;
        }, {}),
        false,
        this.sortUserStatus
      );

      this.userBuyerData = this.formatReduceWithValue(
        users.reduce((count, user) => {
          if (user.roles.some(role => role.getName() === RoleEnum.Landowner)) {
            let buyerName = user.preferredBuyer?.name || 'Generic';
            count[buyerName] = (count[buyerName] || 0) + 1;
          }
          return count;
        }, {})
      );
      this.spinnerService.hide('user-spinner');
    });
    this.spinnerService.show('site-spinner', true, 'Loading Sites');
    this.siteService.getSitesForStatDashboard().subscribe(response => {
      this.sites = response.results;
      this.updateSites(this.sites);
      this.spinnerService.hide('site-spinner');
    });

    this.applyFilterStatus$
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; column: string }) => {
        this.filterStatusState = eventObj.event.value;
        this.updateStats();
      });
    this.applyFilterRegion$
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; section: string }) => {
        this.filterRegionState[eventObj.section] = eventObj.event.value;
        if (eventObj.section == 'valuationSummary') {
          this.updateStats();
        } else {
          this.updateSites(
            this.sites.filter(site => this.filterRegionState[eventObj.section].includes(site.cohort.region.id))
          );
        }
      });
  }

  formatReduceWithValue(obj: any, addItemValue: boolean = true, sortArray: any = null) {
    let returnObj = Object.entries(obj)
      .map(([name, total]) => ({
        name: String(name + (addItemValue ? ` (${total})` : '')),
        value: Number(total),
      }))
      .filter(dataPoint => Number(dataPoint.value) > 0);
    if (sortArray) {
      returnObj.sort((a, b) => {
        return sortArray.indexOf(a.name) - sortArray.indexOf(b.name);
      });
    }
    return returnObj;
  }

  formatMapWithValue(array: any[]) {
    return array
      .map(item => {
        return {
          name: item.name + ` (${item.value})`,
          value: item.value,
        };
      })
      .filter(dataPoint => Number(dataPoint.value) > 0);
  }

  getSiteCounts(sites: Site[], statusList: string[], siteClassification: string) {
    this.totalCounts[siteClassification] = 0;
    return this.formatReduceWithValue(
      sites.reduce((count, site) => {
        if (statusList.includes(site.status)) {
          this.totalCounts[siteClassification]++;
          count[site.status] = (count[site.status] || 0) + 1;
        }
        return count;
      }, {}),
      true,
      statusList
    );
  }

  updateSites(sites: any) {
    this.totalCounts['Sites'] = sites.length;
    this.totalCounts['Pending'] = 0;
    this.pendingSiteData = this.formatReduceWithValue(
      sites.reduce((count: any, site: Site) => {
        if (this.siteHelper.sitePendingStatusList.includes(site.status)) {
          this.totalCounts['Pending']++;
          if (site.status === StatusEnum.REQUIRES_LANDOWNER_REVISION) {
            count[site.status] = (count[site.status] || 0) + 1;
          } else if (site.currentStep === null) {
            count['Unknown'] = (count['Unknown'] || 0) + 1;
          } else {
            count['Stopped at ' + site.currentStep] = (count['Stopped at ' + site.currentStep] || 0) + 1;
          }
        }
        return count;
      }, {}),
      false,
      this.sortSteps
    );
    this.activeSiteData = this.getSiteCounts(sites, this.siteHelper.siteActiveStatusList, 'Active');
    this.submittedSiteData = this.getSiteCounts(sites, this.siteHelper.siteSubmittedStatusList, 'Submitted');
    this.inactiveSiteData = this.getSiteCounts(sites, this.siteHelper.siteInactiveStatusList, 'Inactive');
    this.overallSiteData = this.formatMapWithValue([
      { name: 'Pending', value: this.totalCounts['Pending'] },
      { name: 'Active', value: this.totalCounts['Active'] },
      { name: 'Submitted', value: this.totalCounts['Submitted'] },
      { name: 'Inactive', value: this.totalCounts['Inactive'] },
    ]);
    this.updateStats();
  }

  updateStats() {
    let siteStats = this.sites.reduce(
      (stats, site) => {
        if (
          this.filterStatusState.includes(site.status) &&
          this.filterRegionState['valuationSummary'].includes(site.cohort.region.id) &&
          site.submittedDate !== null &&
          site.netErt !== null
        ) {
          stats.total++;
          stats.netErt += site.netErt;
          stats.averageAnnual += site.annualPayment;
          stats.totalAcres += site.area;
        }
        return stats;
      },
      {
        total: 0,
        netErt: 0,
        totalAcres: 0,
        ertPerAcre: 0,
        acresPerSite: 0,
        averageAnnual: 0,
      }
    );
    siteStats.ertPerAcre = siteStats.totalAcres > 0 ? siteStats.netErt / siteStats.totalAcres : 0;
    siteStats.acresPerSite = siteStats.total > 0 ? siteStats.totalAcres / siteStats.total : 0;
    this.siteStats = siteStats;
  }

  applyStatusFilter(event: any, column: string): void {
    this.applyFilterStatus$.next({ event, column });
  }

  applyRegionFilter(event: any, section: string): void {
    this.applyFilterRegion$.next({ event, section });
  }

  onSelect($event: any, tabGroup: MatTabGroup) {
    const tabIndex = tabGroup._tabs
      .toArray()
      .findIndex(tab => tab.textLabel?.includes($event.name.replace(/[^A-Za-z]/g, '')));

    if (tabIndex !== -1) {
      tabGroup.selectedIndex = tabIndex;
    } else {
      console.log('Tab not found');
    }
  }
}
